import { useMutation, useQuery } from '@tanstack/react-query';
import type {
    DataTag,
    DefinedInitialDataOptions,
    DefinedUseQueryResult,
    MutationFunction,
    QueryClient,
    QueryFunction,
    QueryKey,
    UndefinedInitialDataOptions,
    UseMutationOptions,
    UseMutationResult,
    UseQueryOptions,
    UseQueryResult,
} from '@tanstack/react-query';

import { useCallback } from 'react';

import type {
    CreateOrUpdateClientModel,
    GetClientActivityLogParams,
    GetClientByIdParams,
    GetClientByIdResponse,
    GetClientDetailByIdParams,
    GetClientDetailsByIdResponse,
    GetClientsParams,
    GetClientsResponse,
    GetUserActivityResponse,
    ProblemDetails,
    SearchClientsParams,
    SearchClientsResponse,
    UploadClientBackgroundImageBody,
    UploadClientPictureBody,
} from '../models';

import { useCustomIDSClient } from '../../custom-client';
import type { ErrorType } from '../../custom-client';

/**
 * Get all Clients<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/clients.read
 */
export const useGetClientsHook = () => {
    const getClients = useCustomIDSClient<GetClientsResponse>();

    return useCallback(
        (params?: GetClientsParams, signal?: AbortSignal) => {
            return getClients({ url: `/clients`, method: 'GET', params, signal });
        },
        [getClients],
    );
};

export const getGetClientsQueryKey = (params?: GetClientsParams) => {
    return [`/clients`, ...(params ? [params] : [])] as const;
};

export const useGetClientsQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetClientsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: GetClientsParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetClientsHook>>>, TError, TData>>;
    },
) => {
    const { query: queryOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetClientsQueryKey(params);

    const getClients = useGetClientsHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetClientsHook>>>> = ({ signal }) =>
        getClients(params, signal);

    return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetClientsHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetClientsQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useGetClientsHook>>>>;
export type GetClientsQueryError = ErrorType<ProblemDetails>;

export function useGetClients<
    TData = Awaited<ReturnType<ReturnType<typeof useGetClientsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params: undefined | GetClientsParams,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetClientsHook>>>, TError, TData>>
            & Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetClientsHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetClientsHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetClients<
    TData = Awaited<ReturnType<ReturnType<typeof useGetClientsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: GetClientsParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetClientsHook>>>, TError, TData>>
            & Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetClientsHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetClientsHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetClients<
    TData = Awaited<ReturnType<ReturnType<typeof useGetClientsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: GetClientsParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetClientsHook>>>, TError, TData>>;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

export function useGetClients<
    TData = Awaited<ReturnType<ReturnType<typeof useGetClientsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: GetClientsParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetClientsHook>>>, TError, TData>>;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetClientsQueryOptions(params, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Returns the basic information of a client<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/clients.read
 */
export const useGetClientByIdHook = () => {
    const getClientById = useCustomIDSClient<GetClientByIdResponse>();

    return useCallback(
        (id: string, params?: GetClientByIdParams, signal?: AbortSignal) => {
            return getClientById({ url: `/clients/${id}`, method: 'GET', params, signal });
        },
        [getClientById],
    );
};

export const getGetClientByIdQueryKey = (id: string, params?: GetClientByIdParams) => {
    return [`/clients/${id}`, ...(params ? [params] : [])] as const;
};

export const useGetClientByIdQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetClientByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetClientByIdParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetClientByIdHook>>>, TError, TData>>;
    },
) => {
    const { query: queryOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetClientByIdQueryKey(id, params);

    const getClientById = useGetClientByIdHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetClientByIdHook>>>> = ({ signal }) =>
        getClientById(id, params, signal);

    return { queryKey, queryFn, enabled: !!id, ...queryOptions } as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetClientByIdHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetClientByIdQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useGetClientByIdHook>>>>;
export type GetClientByIdQueryError = ErrorType<ProblemDetails>;

export function useGetClientById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetClientByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params: undefined | GetClientByIdParams,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetClientByIdHook>>>, TError, TData>>
            & Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetClientByIdHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetClientByIdHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetClientById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetClientByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetClientByIdParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetClientByIdHook>>>, TError, TData>>
            & Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetClientByIdHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetClientByIdHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetClientById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetClientByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetClientByIdParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetClientByIdHook>>>, TError, TData>>;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

export function useGetClientById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetClientByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetClientByIdParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetClientByIdHook>>>, TError, TData>>;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetClientByIdQueryOptions(id, params, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Updates an existing Client<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/clients.write
 */
export const useUpdateClientHook = () => {
    const updateClient = useCustomIDSClient<GetClientDetailsByIdResponse>();

    return useCallback(
        (id: string, createOrUpdateClientModel: CreateOrUpdateClientModel, signal?: AbortSignal) => {
            return updateClient({
                url: `/clients/${id}`,
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                data: createOrUpdateClientModel,
                signal,
            });
        },
        [updateClient],
    );
};

export const useUpdateClientMutationOptions = <TError = ErrorType<ProblemDetails>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useUpdateClientHook>>>,
        TError,
        { id: string; data: CreateOrUpdateClientModel },
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useUpdateClientHook>>>,
    TError,
    { id: string; data: CreateOrUpdateClientModel },
    TContext
> => {
    const mutationKey = ['updateClient'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const updateClient = useUpdateClientHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useUpdateClientHook>>>,
        { id: string; data: CreateOrUpdateClientModel }
    > = (props) => {
        const { id, data } = props ?? {};

        return updateClient(id, data);
    };

    return { mutationFn, ...mutationOptions };
};

export type UpdateClientMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useUpdateClientHook>>>>;
export type UpdateClientMutationBody = CreateOrUpdateClientModel;
export type UpdateClientMutationError = ErrorType<ProblemDetails>;

export const useUpdateClient = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useUpdateClientHook>>>,
            TError,
            { id: string; data: CreateOrUpdateClientModel },
            TContext
        >;
    },
    queryClient?: QueryClient,
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useUpdateClientHook>>>,
    TError,
    { id: string; data: CreateOrUpdateClientModel },
    TContext
> => {
    return useMutation(useUpdateClientMutationOptions(options), queryClient);
};
/**
 * Deletes a client<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/clients.write
 */
export const useDeleteClientHook = () => {
    const deleteClient = useCustomIDSClient<void>();

    return useCallback(
        (id: string, signal?: AbortSignal) => {
            return deleteClient({ url: `/clients/${id}`, method: 'DELETE', signal });
        },
        [deleteClient],
    );
};

export const useDeleteClientMutationOptions = <TError = ErrorType<ProblemDetails>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useDeleteClientHook>>>,
        TError,
        { id: string },
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useDeleteClientHook>>>,
    TError,
    { id: string },
    TContext
> => {
    const mutationKey = ['deleteClient'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const deleteClient = useDeleteClientHook();

    const mutationFn: MutationFunction<Awaited<ReturnType<ReturnType<typeof useDeleteClientHook>>>, { id: string }> = (
        props,
    ) => {
        const { id } = props ?? {};

        return deleteClient(id);
    };

    return { mutationFn, ...mutationOptions };
};

export type DeleteClientMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useDeleteClientHook>>>>;

export type DeleteClientMutationError = ErrorType<ProblemDetails>;

export const useDeleteClient = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useDeleteClientHook>>>,
            TError,
            { id: string },
            TContext
        >;
    },
    queryClient?: QueryClient,
): UseMutationResult<Awaited<ReturnType<ReturnType<typeof useDeleteClientHook>>>, TError, { id: string }, TContext> => {
    return useMutation(useDeleteClientMutationOptions(options), queryClient);
};
/**
 * Provides the activity log for a client<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/clients.read
 */
export const useGetClientActivityLogHook = () => {
    const getClientActivityLog = useCustomIDSClient<GetUserActivityResponse>();

    return useCallback(
        (id: string, params?: GetClientActivityLogParams, signal?: AbortSignal) => {
            return getClientActivityLog({ url: `/clients/${id}/activity-log`, method: 'GET', params, signal });
        },
        [getClientActivityLog],
    );
};

export const getGetClientActivityLogQueryKey = (id: string, params?: GetClientActivityLogParams) => {
    return [`/clients/${id}/activity-log`, ...(params ? [params] : [])] as const;
};

export const useGetClientActivityLogQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetClientActivityLogHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetClientActivityLogParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetClientActivityLogHook>>>, TError, TData>
        >;
    },
) => {
    const { query: queryOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetClientActivityLogQueryKey(id, params);

    const getClientActivityLog = useGetClientActivityLogHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetClientActivityLogHook>>>> = ({ signal }) =>
        getClientActivityLog(id, params, signal);

    return { queryKey, queryFn, enabled: !!id, ...queryOptions } as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetClientActivityLogHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetClientActivityLogQueryResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useGetClientActivityLogHook>>>
>;
export type GetClientActivityLogQueryError = ErrorType<ProblemDetails>;

export function useGetClientActivityLog<
    TData = Awaited<ReturnType<ReturnType<typeof useGetClientActivityLogHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params: undefined | GetClientActivityLogParams,
    options: {
        query: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetClientActivityLogHook>>>, TError, TData>
        >
            & Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetClientActivityLogHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetClientActivityLogHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetClientActivityLog<
    TData = Awaited<ReturnType<ReturnType<typeof useGetClientActivityLogHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetClientActivityLogParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetClientActivityLogHook>>>, TError, TData>
        >
            & Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetClientActivityLogHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetClientActivityLogHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetClientActivityLog<
    TData = Awaited<ReturnType<ReturnType<typeof useGetClientActivityLogHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetClientActivityLogParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetClientActivityLogHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

export function useGetClientActivityLog<
    TData = Awaited<ReturnType<ReturnType<typeof useGetClientActivityLogHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetClientActivityLogParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetClientActivityLogHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetClientActivityLogQueryOptions(id, params, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Uploads a login background image for an existing client<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/clients.write
 */
export const useUploadClientBackgroundImageHook = () => {
    const uploadClientBackgroundImage = useCustomIDSClient<void>();

    return useCallback(
        (id: string, uploadClientBackgroundImageBody: UploadClientBackgroundImageBody, signal?: AbortSignal) => {
            const formData = new FormData();
            if (uploadClientBackgroundImageBody.File !== undefined) {
                formData.append(`File`, uploadClientBackgroundImageBody.File);
            }

            return uploadClientBackgroundImage({
                url: `/clients/${id}/background-image`,
                method: 'PUT',
                data: formData,
                signal,
            });
        },
        [uploadClientBackgroundImage],
    );
};

export const useUploadClientBackgroundImageMutationOptions = <
    TError = ErrorType<ProblemDetails>,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useUploadClientBackgroundImageHook>>>,
        TError,
        { id: string; data: UploadClientBackgroundImageBody },
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useUploadClientBackgroundImageHook>>>,
    TError,
    { id: string; data: UploadClientBackgroundImageBody },
    TContext
> => {
    const mutationKey = ['uploadClientBackgroundImage'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const uploadClientBackgroundImage = useUploadClientBackgroundImageHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useUploadClientBackgroundImageHook>>>,
        { id: string; data: UploadClientBackgroundImageBody }
    > = (props) => {
        const { id, data } = props ?? {};

        return uploadClientBackgroundImage(id, data);
    };

    return { mutationFn, ...mutationOptions };
};

export type UploadClientBackgroundImageMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useUploadClientBackgroundImageHook>>>
>;
export type UploadClientBackgroundImageMutationBody = UploadClientBackgroundImageBody;
export type UploadClientBackgroundImageMutationError = ErrorType<ProblemDetails>;

export const useUploadClientBackgroundImage = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useUploadClientBackgroundImageHook>>>,
            TError,
            { id: string; data: UploadClientBackgroundImageBody },
            TContext
        >;
    },
    queryClient?: QueryClient,
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useUploadClientBackgroundImageHook>>>,
    TError,
    { id: string; data: UploadClientBackgroundImageBody },
    TContext
> => {
    return useMutation(useUploadClientBackgroundImageMutationOptions(options), queryClient);
};
/**
 * Deletes a background image for an existing client<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/clients.write
 */
export const useDeleteClientBackgroundImageHook = () => {
    const deleteClientBackgroundImage = useCustomIDSClient<void>();

    return useCallback(
        (id: string, signal?: AbortSignal) => {
            return deleteClientBackgroundImage({ url: `/clients/${id}/background-image`, method: 'DELETE', signal });
        },
        [deleteClientBackgroundImage],
    );
};

export const useDeleteClientBackgroundImageMutationOptions = <
    TError = ErrorType<ProblemDetails>,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useDeleteClientBackgroundImageHook>>>,
        TError,
        { id: string },
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useDeleteClientBackgroundImageHook>>>,
    TError,
    { id: string },
    TContext
> => {
    const mutationKey = ['deleteClientBackgroundImage'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const deleteClientBackgroundImage = useDeleteClientBackgroundImageHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useDeleteClientBackgroundImageHook>>>,
        { id: string }
    > = (props) => {
        const { id } = props ?? {};

        return deleteClientBackgroundImage(id);
    };

    return { mutationFn, ...mutationOptions };
};

export type DeleteClientBackgroundImageMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useDeleteClientBackgroundImageHook>>>
>;

export type DeleteClientBackgroundImageMutationError = ErrorType<ProblemDetails>;

export const useDeleteClientBackgroundImage = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useDeleteClientBackgroundImageHook>>>,
            TError,
            { id: string },
            TContext
        >;
    },
    queryClient?: QueryClient,
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useDeleteClientBackgroundImageHook>>>,
    TError,
    { id: string },
    TContext
> => {
    return useMutation(useDeleteClientBackgroundImageMutationOptions(options), queryClient);
};
/**
 * Uploads a client picture for an existing client<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/clients.write
 */
export const useUploadClientPictureHook = () => {
    const uploadClientPicture = useCustomIDSClient<void>();

    return useCallback(
        (id: string, uploadClientPictureBody: UploadClientPictureBody, signal?: AbortSignal) => {
            const formData = new FormData();
            if (uploadClientPictureBody.File !== undefined) {
                formData.append(`File`, uploadClientPictureBody.File);
            }

            return uploadClientPicture({ url: `/clients/${id}/client-picture`, method: 'PUT', data: formData, signal });
        },
        [uploadClientPicture],
    );
};

export const useUploadClientPictureMutationOptions = <
    TError = ErrorType<ProblemDetails>,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useUploadClientPictureHook>>>,
        TError,
        { id: string; data: UploadClientPictureBody },
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useUploadClientPictureHook>>>,
    TError,
    { id: string; data: UploadClientPictureBody },
    TContext
> => {
    const mutationKey = ['uploadClientPicture'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const uploadClientPicture = useUploadClientPictureHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useUploadClientPictureHook>>>,
        { id: string; data: UploadClientPictureBody }
    > = (props) => {
        const { id, data } = props ?? {};

        return uploadClientPicture(id, data);
    };

    return { mutationFn, ...mutationOptions };
};

export type UploadClientPictureMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useUploadClientPictureHook>>>
>;
export type UploadClientPictureMutationBody = UploadClientPictureBody;
export type UploadClientPictureMutationError = ErrorType<ProblemDetails>;

export const useUploadClientPicture = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useUploadClientPictureHook>>>,
            TError,
            { id: string; data: UploadClientPictureBody },
            TContext
        >;
    },
    queryClient?: QueryClient,
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useUploadClientPictureHook>>>,
    TError,
    { id: string; data: UploadClientPictureBody },
    TContext
> => {
    return useMutation(useUploadClientPictureMutationOptions(options), queryClient);
};
/**
 * Returns the detailed information of a client<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/clients.read
 */
export const useGetClientDetailByIdHook = () => {
    const getClientDetailById = useCustomIDSClient<GetClientDetailsByIdResponse>();

    return useCallback(
        (id: string, params?: GetClientDetailByIdParams, signal?: AbortSignal) => {
            return getClientDetailById({ url: `/clients/${id}/detail`, method: 'GET', params, signal });
        },
        [getClientDetailById],
    );
};

export const getGetClientDetailByIdQueryKey = (id: string, params?: GetClientDetailByIdParams) => {
    return [`/clients/${id}/detail`, ...(params ? [params] : [])] as const;
};

export const useGetClientDetailByIdQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetClientDetailByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetClientDetailByIdParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetClientDetailByIdHook>>>, TError, TData>
        >;
    },
) => {
    const { query: queryOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetClientDetailByIdQueryKey(id, params);

    const getClientDetailById = useGetClientDetailByIdHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetClientDetailByIdHook>>>> = ({ signal }) =>
        getClientDetailById(id, params, signal);

    return { queryKey, queryFn, enabled: !!id, ...queryOptions } as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetClientDetailByIdHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetClientDetailByIdQueryResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useGetClientDetailByIdHook>>>
>;
export type GetClientDetailByIdQueryError = ErrorType<ProblemDetails>;

export function useGetClientDetailById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetClientDetailByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params: undefined | GetClientDetailByIdParams,
    options: {
        query: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetClientDetailByIdHook>>>, TError, TData>
        >
            & Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetClientDetailByIdHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetClientDetailByIdHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetClientDetailById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetClientDetailByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetClientDetailByIdParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetClientDetailByIdHook>>>, TError, TData>
        >
            & Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetClientDetailByIdHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetClientDetailByIdHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetClientDetailById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetClientDetailByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetClientDetailByIdParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetClientDetailByIdHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

export function useGetClientDetailById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetClientDetailByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetClientDetailByIdParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetClientDetailByIdHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetClientDetailByIdQueryOptions(id, params, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Moves a client to another application<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/clients.write<br>login.umdaschgroup.com/applications.write
 */
export const useMoveClientHook = () => {
    const moveClient = useCustomIDSClient<void>();

    return useCallback(
        (id: string, applicationId: string, signal?: AbortSignal) => {
            return moveClient({ url: `/clients/${id}/move/${applicationId}`, method: 'POST', signal });
        },
        [moveClient],
    );
};

export const useMoveClientMutationOptions = <TError = ErrorType<ProblemDetails>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useMoveClientHook>>>,
        TError,
        { id: string; applicationId: string },
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useMoveClientHook>>>,
    TError,
    { id: string; applicationId: string },
    TContext
> => {
    const mutationKey = ['moveClient'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const moveClient = useMoveClientHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useMoveClientHook>>>,
        { id: string; applicationId: string }
    > = (props) => {
        const { id, applicationId } = props ?? {};

        return moveClient(id, applicationId);
    };

    return { mutationFn, ...mutationOptions };
};

export type MoveClientMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useMoveClientHook>>>>;

export type MoveClientMutationError = ErrorType<ProblemDetails>;

export const useMoveClient = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useMoveClientHook>>>,
            TError,
            { id: string; applicationId: string },
            TContext
        >;
    },
    queryClient?: QueryClient,
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useMoveClientHook>>>,
    TError,
    { id: string; applicationId: string },
    TContext
> => {
    return useMutation(useMoveClientMutationOptions(options), queryClient);
};
/**
 * Search clients by name and grant types<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/clients.read
 */
export const useSearchClientsHook = () => {
    const searchClients = useCustomIDSClient<SearchClientsResponse>();

    return useCallback(
        (params?: SearchClientsParams, signal?: AbortSignal) => {
            return searchClients({ url: `/clients/search`, method: 'GET', params, signal });
        },
        [searchClients],
    );
};

export const getSearchClientsQueryKey = (params?: SearchClientsParams) => {
    return [`/clients/search`, ...(params ? [params] : [])] as const;
};

export const useSearchClientsQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useSearchClientsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: SearchClientsParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useSearchClientsHook>>>, TError, TData>>;
    },
) => {
    const { query: queryOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getSearchClientsQueryKey(params);

    const searchClients = useSearchClientsHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useSearchClientsHook>>>> = ({ signal }) =>
        searchClients(params, signal);

    return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useSearchClientsHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type SearchClientsQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useSearchClientsHook>>>>;
export type SearchClientsQueryError = ErrorType<ProblemDetails>;

export function useSearchClients<
    TData = Awaited<ReturnType<ReturnType<typeof useSearchClientsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params: undefined | SearchClientsParams,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useSearchClientsHook>>>, TError, TData>>
            & Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useSearchClientsHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useSearchClientsHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useSearchClients<
    TData = Awaited<ReturnType<ReturnType<typeof useSearchClientsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: SearchClientsParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useSearchClientsHook>>>, TError, TData>>
            & Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useSearchClientsHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useSearchClientsHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useSearchClients<
    TData = Awaited<ReturnType<ReturnType<typeof useSearchClientsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: SearchClientsParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useSearchClientsHook>>>, TError, TData>>;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

export function useSearchClients<
    TData = Awaited<ReturnType<ReturnType<typeof useSearchClientsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: SearchClientsParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useSearchClientsHook>>>, TError, TData>>;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useSearchClientsQueryOptions(params, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return { ...query, queryKey: queryOptions.queryKey };
}
